import { forwardRef, useEffect } from 'react'
import { Box, Flex, Text, Input, type InputProps } from '@chakra-ui/react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useFormColors } from './FormThemeContext'

interface FormFieldProps extends Omit<InputProps, 'name'> {
  name: string
  label: string
  required?: boolean
  helperText?: string
}

export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
  function FormField({ name, label, required, helperText, maxLength, ...inputProps }, ref) {
    const { register, formState: { errors }, setValue, control } = useFormContext()
    const c = useFormColors()

    const error = errors[name]
    const hasError = Boolean(error)

    const watched = useWatch({ control, name }) as unknown
    const valueStr =
      typeof watched === 'string' ? watched : watched == null ? '' : String(watched)

    // Truncate values that arrive longer than maxLength (programmatic setValue
    // from a fetch response, etc.) so the form value stays within bounds.
    useEffect(() => {
      if (typeof maxLength !== 'number') return
      if (valueStr.length <= maxLength) return
      setValue(name, valueStr.slice(0, maxLength), { shouldDirty: false, shouldValidate: false })
    }, [maxLength, valueStr, name, setValue])

    const showCounter = typeof maxLength === 'number'
    const counterCount = Math.min(valueStr.length, maxLength ?? 0)

    const baseStyles = {
      bg: c.bgInput,
      border: '1px solid',
      borderColor: c.border,
      borderRadius: 'xl',
      color: c.text,
      _placeholder: { color: c.textMuted },
      _focus: {
        outline: 'none',
        ring: '1px',
        ringColor: hasError ? c.error : c.accent,
        borderColor: hasError ? c.error : c.accent,
      },
    } as const

    const errorStyles = hasError
      ? { ring: '1px', ringColor: c.error, borderColor: c.error }
      : {}

    return (
      <Box>
        <Text fontSize="sm" fontWeight="500" color={c.textSecondary} mb={2}>
          {label}{required && <Text as="span" color={c.error} ml={0.5}>*</Text>}
        </Text>
        <Input
          {...register(name)}
          ref={ref}
          {...baseStyles}
          {...errorStyles}
          maxLength={maxLength}
          {...inputProps}
        />
        {(hasError || helperText || showCounter) && (
          <Flex justifyContent="space-between" alignItems="flex-start" mt={1} gap={2}>
            <Box flex="1" minW={0}>
              {hasError && error?.message && (
                <Text fontSize="xs" color={c.error}>
                  {error.message as string}
                </Text>
              )}
              {helperText && !hasError && (
                <Text fontSize="xs" color={c.textMuted}>
                  {helperText}
                </Text>
              )}
            </Box>
            {showCounter && (
              <Text
                fontSize="xs"
                color={counterCount >= (maxLength as number) ? c.error : c.textMuted}
                flexShrink={0}
                fontVariantNumeric="tabular-nums"
              >
                {counterCount}/{maxLength}
              </Text>
            )}
          </Flex>
        )}
      </Box>
    )
  }
)
